import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowUpRight, FileText, RefreshCw } from 'lucide-react';
import { useListLeadForms } from '../api';
import type { LeadForm } from '../api/types';

interface LeadFormSelectorProps {
  value?: string;
  metaPageId?: string;
  id?: string;
  onChange: (formId: string | undefined) => void;
  onManageForms?: () => void;
  placeholder?: string;
  disabled?: boolean;
  /** Only show forms with these statuses */
  statusFilter?: ('draft' | 'synced')[];
}

export function LeadFormSelector({
  value,
  metaPageId,
  id,
  onChange,
  onManageForms,
  placeholder = 'Select a lead form',
  disabled,
  statusFilter = ['synced'],
}: LeadFormSelectorProps) {
  const { leadForms, isLoading, isError, refetch } = useListLeadForms({
    limit: 100,
  });

  const filteredForms = leadForms.filter(
    (form) =>
      statusFilter.includes(form.status as 'draft' | 'synced') &&
      (!metaPageId || form.metaPageId === metaPageId)
  );

  if (isLoading) {
    return <Skeleton className="h-10 w-full" />;
  }

  if (isError) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-destructive">Failed to load forms</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => refetch()}
        >
          <RefreshCw className="size-4 mr-1" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 gap-2">
      <Select
        value={value || ''}
        onValueChange={(val) => onChange(val || undefined)}
        disabled={disabled}
      >
        <SelectTrigger
          id={id}
          aria-label="Lead form"
          className="min-w-0 flex-1 [&_[data-slot=select-value]]:min-w-0"
        >
          <SelectValue placeholder={placeholder}>
            <span className="truncate">
              {filteredForms.find((form) => form.id === value)?.name}
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {filteredForms.length === 0 ? (
              <div className="p-2 text-center text-sm text-muted-foreground">
                No lead forms available
              </div>
            ) : (
              filteredForms.map((form) => (
                <SelectItem key={form.id} value={form.id}>
                  <div className="flex min-w-0 items-center gap-2">
                    <FileText className="size-4 text-muted-foreground" />
                    <span className="truncate">{form.name}</span>
                    {form.metaFormId ? (
                      <span className="text-xs text-muted-foreground">
                        (synced)
                      </span>
                    ) : null}
                  </div>
                </SelectItem>
              ))
            )}
          </SelectGroup>
        </SelectContent>
      </Select>
      {onManageForms ? (
        <Button
          type="button"
          variant="outline"
          onClick={onManageForms}
          disabled={disabled}
        >
          <ArrowUpRight className="size-4 mr-1" />
          Lead forms
        </Button>
      ) : null}
    </div>
  );
}

export type { LeadForm };
